import { getInstanceCount, qualifiedKey, sectionMinInstances } from '@bmo/form-core';

import type { FormSection, WorkflowField, WorkflowTemplate } from '@/api/workflows';

import { isOptionOffered, type OfferLanguages } from './clientLanguage';
import type { FieldValue } from './FieldRenderer';

/**
 * "Selected by default" (QA D-17 / D-42 — RDSP's Canadian Resident = Yes, "$ value").
 *
 * A default is a pre-filled ANSWER, not a display hint: once written it is an ordinary value that
 * reaches the review, the snapshot and the PDFs. That is why the rules about WHEN it may be
 * written live with the caller (FormShell), which owns what the advisor has seen — the same
 * `shownStepsRef` / `restoredValuesRef` / `editedFieldsRef` gate the cross-step prefill uses.
 * This module only knows WHAT the default is and where it goes.
 */

/** Single-pick types — a default is one answer, so only these carry one. */
const DEFAULT_CAPABLE = new Set(['RadioGroup', 'SingleSelect']);

/**
 * The value a field starts with, or null. Null when the field carries no default, or when its
 * default is an option the client's language does not offer (D-30): a French client is never
 * handed an answer they could not have picked.
 */
export function defaultOptionValue(
  field: WorkflowField,
  clientLanguage: OfferLanguages,
): string | null {
  if (!DEFAULT_CAPABLE.has(field.type) || field.advisorRepCodeSelector) return null;
  const option = field.options?.find((o) => o.isDefault);
  if (!option || !isOptionOffered(option, clientLanguage)) return null;
  return option.value;
}

function isEmpty(v: FieldValue | undefined): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

type FillGate = {
  /** The client's language — decides whether a scoped default is offered at all. */
  clientLanguage: OfferLanguages;
  /** A step the advisor has already been shown: never filled, even when empty. */
  skipStep?: (stepId: string) => boolean;
  /** A slot that must not be filled for a reason of its own (edited, restored from a save). */
  skipKey?: (key: string) => boolean;
};

/** Fill one section instance's empty default-carrying slots, reporting each through `write`. */
function fillSection(
  section: FormSection,
  instanceIdx: number,
  source: Record<string, FieldValue>,
  gate: FillGate,
  write: (key: string, value: string) => void,
): void {
  for (const row of section.rows) {
    for (const col of row.columns) {
      const field = col.field;
      if (!field) continue;
      const value = defaultOptionValue(field, gate.clientLanguage);
      if (value === null) continue;
      const key = section.repeatable ? qualifiedKey(section.id, instanceIdx, field.id) : field.id;
      if (gate.skipKey?.(key)) continue;
      if (!isEmpty(source[key])) continue;
      write(key, value);
    }
  }
}

/**
 * Fill every EMPTY default-carrying slot across the workflow's form steps — every current
 * instance of a repeated section — except where the gate says no. Never overwrites an answer.
 * Returns the SAME object when nothing was written, so it is safe inside a state updater.
 */
export function applyDefaultOptions(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  gate: FillGate,
): Record<string, FieldValue> {
  let next = values;
  const write = (key: string, value: string) => {
    if (next === values) next = { ...values };
    next[key] = value;
  };
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    if (gate.skipStep?.(step.id)) continue;
    for (const section of step.sections ?? []) {
      const count = section.repeatable
        ? getInstanceCount(values, section.id, sectionMinInstances(section))
        : 1;
      for (let i = 0; i < count; i++) fillSection(section, i, values, gate, write);
    }
  }
  return next;
}

/**
 * Fill the defaults of ONE freshly added repeatable instance. A new instance has by
 * construction never been shown, so the only gate is emptiness — which also means an instance
 * re-added after a removal starts from its defaults, like the first one did.
 */
export function applySectionInstanceDefaults(
  section: FormSection,
  instanceIdx: number,
  values: Record<string, FieldValue>,
  clientLanguage: OfferLanguages,
): Record<string, FieldValue> {
  let next = values;
  fillSection(section, instanceIdx, values, { clientLanguage }, (key, value) => {
    if (next === values) next = { ...values };
    next[key] = value;
  });
  return next;
}
